import React from "react";
import PropTypes from "prop-types";
import { isEmpty, isNil } from "ramda";
import clsx from "clsx";
import { Typography, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import PersonPresenter from "presenters/PersonPresenter";
import FacePresenter from "presenters/FacePresenter";
import PersonLabel from "components/PersonLabel";
import Balloon from "assets/images/occasions/iconsComponents/Balloon";
import Heart from "assets/images/occasions/iconsComponents/Heart";
import flowerBouquet from "assets/images/occasions/flowerBouquet.svg";
import flower from "assets/images/occasions/flower.svg";
import hat from "assets/images/occasions/hat.svg";
import { DATE_TYPE } from "enums";
import CTA from "components/CTA";
import partyHat from "assets/images/party-hat.png";
import { useScreen } from "hooks";

import DisplayFace from "../DisplayFace/DisplayFace";

import useStyles from "./useStyles";

const DisplayPerson = (props) => {
  const classes = useStyles();
  const {
    showLabel,
    isCTA,
    person,
    face,
    onClick,
    relation,
    onCounterClick,
    onCheck,
    faceSelected,
    hasPartyHat,
    showBadge,
    variantCTA,
    noOpacity,
    dateType,
    showCheck,
  } = props;
  const totalHeads = PersonPresenter.totalHeads(person) || 0;
  const { isDesktop, isMobile } = useScreen();

  const handleClick = () => {
    if (isNil(onClick)) return;

    onClick(person);
  };

  const getHat = () => {
    if (dateType === DATE_TYPE.fathersDay) {
      return <img src={hat} className={classes.fatherHat} alt="FatherHat" />;
    }
    return hasPartyHat && <img src={partyHat} className={classes.partyHat} alt="PartyHat" />;
  };

  const getPersonDecoration = () => {
    switch (dateType) {
      case DATE_TYPE.birthday:
        return (
          <div>
            <Balloon
              style={{ bottom: 0, left: -20 }}
              className={clsx(classes.decor, classes.balloon)}
              color="#A3EAEB"
            />
            <Balloon style={{ top: 0, left: 25 }} className={clsx(classes.decor, classes.balloon)} color="#E5CBFC" />
            <Balloon style={{ top: 20, right: 5 }} className={clsx(classes.decor, classes.balloon)} color="#F578C2" />
            <Balloon
              style={{ bottom: -46, right: -16 }}
              className={clsx(classes.decor, classes.balloon, classes.blueBalloon)}
              color="#5192FF"
            />
          </div>
        );
      case DATE_TYPE.valentines:
        return (
          <div>
            <Heart
              style={{ bottom: isDesktop ? 70 : 40, left: isDesktop ? -20 : 0 }}
              className={clsx(classes.decor, classes.smallHeart)}
            />
            <Heart
              style={{ top: isDesktop ? -40 : -10, left: isDesktop ? 25 : 20 }}
              className={clsx(classes.decor, classes.bigHeart)}
            />
            <Heart style={{ top: isDesktop ? -20 : 15, right: 5 }} className={clsx(classes.decor, classes.bigHeart)} />
            <Heart
              style={{ bottom: isDesktop ? 30 : 5, right: 0 }}
              className={clsx(classes.decor, classes.smallHeart)}
            />
          </div>
        );
      case DATE_TYPE.anniversary:
        return (
          <div>
            <img
              alt="Flower Bouquet"
              src={flowerBouquet}
              style={{
                transform: "scaleX(-1)",
                top: isMobile ? 7 : 35,
                left: -20,
              }}
              className={clsx(classes.decor, classes.flowerBouquet)}
            />
            <img
              alt="Flower Bouquet"
              src={flowerBouquet}
              style={{
                top: isMobile ? 7 : 35,
                right: -20,
              }}
              className={clsx(classes.decor, classes.flowerBouquet)}
            />
          </div>
        );
      case DATE_TYPE.mothersDay:
        return (
          <div>
            <img
              alt="Flower"
              style={{ bottom: isDesktop ? 50 : 30, left: isDesktop ? -20 : -10 }}
              src={flower}
              className={clsx(classes.decor, classes.flower)}
            />
            <img
              alt="Flower"
              style={{ top: isDesktop ? -40 : -10, left: isDesktop ? 25 : 20 }}
              src={flower}
              className={clsx(classes.decor, classes.flower)}
            />
            <img
              alt="Flower"
              src={flower}
              style={{ top: isDesktop ? -20 : 15, right: 5 }}
              className={clsx(classes.decor, classes.flower)}
            />
            <img
              alt="Flower"
              src={flower}
              style={{ bottom: isDesktop ? 30 : 5, right: isDesktop ? -20 : 0 }}
              className={clsx(classes.decor, classes.flower)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const handleCheck = () => {
    if (showCheck) {
      let checkedFace;

      if (!isNil(person)) checkedFace = person;
      if (!isNil(face)) checkedFace = face;

      onCheck(isEmpty(faceSelected) || faceSelected !== checkedFace ? checkedFace : {});
    }
  };

  const isFaceSelected = faceSelected === person || faceSelected === face;

  return (
    <Box
      data-testid={`displayPerson-${PersonPresenter.id(person) || FacePresenter.id(face)}`}
      className={clsx(classes.root, { [classes.noOpacity]: noOpacity, [classes.extended]: !isNil(dateType) })}
      onClick={handleCheck}
    >
      {getPersonDecoration()}
      {!showCheck && showBadge && totalHeads > 1 && (
        <Typography
          component="span"
          variant="caption"
          data-testid={PersonPresenter.id(person)}
          className={clsx(classes.badge, classes.blueBadge)}
          onClick={onCounterClick ? () => onCounterClick(person) : handleClick}
          role="presentation"
        >
          <Typography component="small">{totalHeads > 99 ? "99+" : totalHeads}</Typography>
        </Typography>
      )}
      {showCheck && isFaceSelected ? (
        <Typography
          component="span"
          data-testid={`check-${PersonPresenter.id(person) || FacePresenter.id(face)}`}
          variant="caption"
          className={clsx(classes.badge, classes.greenBadge)}
          role="presentation"
        >
          <CheckCircleIcon />
        </Typography>
      ) : null}
      {getHat()}
      {isCTA ? (
        <CTA relation={relation} showLabel={showLabel} variant={variantCTA} onClick={onClick} />
      ) : (
        <>
          <DisplayFace
            clickable={!isNil(onClick) || !isNil(onCheck)}
            face={isNil(face) ? PersonPresenter.defaultHead(person) : face}
            onClick={handleClick}
          />
          {showLabel && !isNil(person) && (
            <PersonLabel
              clickable={!isNil(onClick) || !isNil(onCheck)}
              label={PersonPresenter.label(person)}
              relation={PersonPresenter.relation(person)}
              onClick={handleClick}
            />
          )}
        </>
      )}
    </Box>
  );
};

DisplayPerson.propTypes = {
  hasPartyHat: PropTypes.bool,
  isCTA: PropTypes.bool,
  onClick: PropTypes.func,
  onCheck: PropTypes.func,
  faceSelected: PersonPresenter.shape() || FacePresenter.shape(),
  onCounterClick: PropTypes.func,
  person: PersonPresenter.shape(),
  face: FacePresenter.shape(),
  relation: PropTypes.string,
  showBadge: PropTypes.bool,
  showLabel: PropTypes.bool,
  variantCTA: PropTypes.string,
  noOpacity: PropTypes.bool,
  dateType: PropTypes.string,
  showCheck: PropTypes.bool,
};

DisplayPerson.defaultProps = {
  isCTA: false,
  showBadge: true,
  showLabel: true,
  hasPartyHat: false,
  noOpacity: false,
  showCheck: false,
};

export default DisplayPerson;
